import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import walletApiController from "../../../services/wallet.service";
import { formatWalletMoney, normalizeWalletDetails, walletNextPage } from "../../../services/walletResponse";

const WalletCard = ({ clientId, currency = "AUD" }) => {
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback((page = 1, append = false) => {
    setLoading(true);
    setError("");

    // Balance and contribution plans come from two different routes. A failure
    // in one must not blank the other, so only a failed balance request keeps
    // the wallet error visible.
    const detailsRequest = walletApiController
      .details({ client_id: clientId, page, limit: 50 })
      .then((walletResponse) => normalizeWalletDetails(walletResponse?.data, currency))
      .catch((requestError) => {
        setError(requestError?.response?.data?.message || "Unable to load wallet details.");
        return null;
      });

    const plansRequest = walletApiController
      .plans({ client_id: clientId })
      .then((plansResponse) => {
        const planBody = plansResponse?.data?.data ?? plansResponse?.data ?? {};
        const planRows = Array.isArray(planBody) ? planBody : (planBody.plans || planBody.contributions || []);
        return normalizeWalletDetails({ plans: planRows }).plans;
      })
      .catch(() => []);

    return Promise.all([detailsRequest, plansRequest])
      .then(([details, plans]) => {
        if (!details) return;
        setWallet((current) => append && current
          ? { ...details, plans, transactions: [...current.transactions, ...details.transactions] }
          : { ...details, plans });
      })
      .finally(() => setLoading(false));
  }, [clientId, currency]);

  useEffect(() => { load(); }, [load]);

  const cancelPlan = (planId) => {
    setLoading(true);
    walletApiController.cancel({ id: planId, client_id: clientId }).then(() => load()).catch((requestError) => {
      setError(requestError?.response?.data?.message || "Unable to cancel the contribution plan.");
      setLoading(false);
    });
  };

  if (!wallet && loading) return <div>Loading wallet…</div>;

  return (
    <section aria-labelledby="client-wallet-title">
      <div className="drawer-header"><h2 id="client-wallet-title">Wallet</h2></div>
      {error ? <div className="alert alert-danger" role="alert">{error}</div> : null}
      {wallet ? (
        <div className="content-wrp">
          <div className="card card-body mb-3">
            <div className="text-muted">Available balance</div>
            <strong className="h3">{formatWalletMoney(wallet.available, wallet.currency)}</strong>
            <div>Balance: {formatWalletMoney(wallet.balance, wallet.currency)}</div>
            <div>Reserved: {formatWalletMoney(wallet.reserved, wallet.currency)}</div>
          </div>

          <h3>Contribution plans</h3>
          {wallet.plans.length ? wallet.plans.map((plan) => (
            <div className="card card-body mb-2" key={plan.id}>
              <strong>{formatWalletMoney(plan.amount, wallet.currency)} · {plan.frequency}</strong>
              <span>Status: {plan.status}</span>
              {plan.next_billing_at ? <span>Next billing: {plan.next_billing_at}</span> : null}
              {plan.failure_message ? <div className="text-danger">{plan.failure_message}</div> : null}
              {!['cancelled', 'completed'].includes(plan.status.toLowerCase()) ? (
                <button type="button" className="btn btn-outline-danger mt-2" disabled={loading} onClick={() => cancelPlan(plan.id)}>Cancel contribution</button>
              ) : null}
            </div>
          )) : <p>No active wallet contribution plans.</p>}

          <h3>Transaction history</h3>
          <p className="text-muted">Wallet history is an immutable record of credits, payments and reversals.</p>
          {wallet.transactions.map((transaction) => (
            <div className="border-bottom py-2" key={transaction.id}>
              <strong>{transaction.type === "debit" ? "−" : "+"}{formatWalletMoney(transaction.amount, wallet.currency)}</strong>
              <span className="d-block">{transaction.source || "Wallet transaction"}</span>
              <span className="text-muted">Balance after: {formatWalletMoney(transaction.balance_after, wallet.currency)}</span>
              {transaction.created_at ? <span className="d-block text-muted">{transaction.created_at}</span> : null}
            </div>
          ))}
          {wallet.next_page_url ? <button type="button" className="btn btn-link" disabled={loading} onClick={() => load(walletNextPage(wallet.next_page_url), true)}>Load more</button> : null}
        </div>
      ) : null}
    </section>
  );
};

WalletCard.propTypes = { clientId: PropTypes.number.isRequired, currency: PropTypes.string };
export default WalletCard;
